import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Field';
import { Banner } from '@/components/ui/Feedback';
import { useAuth } from '@/features/auth/AuthContext';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { instrumentApi, type BandInput, type InstrumentDetail } from './api';

interface Row extends BandInput {
  /** Kunci stabil untuk React — nama bisa berubah saat diketik. */
  key: string;
}

export function BandsTab({ data }: { data: InstrumentDetail }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = user?.role === 'super_admin';

  const [rows, setRows] = useState<Row[]>([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const hasIndividualNames = data.version.bands.some((b) => b.nameIndividual);

  useEffect(() => {
    setRows(
      data.version.bands.map((b, i) => ({
        key: `b${i}`,
        gt: b.gt,
        name: b.name,
        nameIndividual: b.nameIndividual,
        characteristic: b.characteristic,
      })),
    );
  }, [data.version.bands]);

  const save = useMutation({
    mutationFn: () =>
      instrumentApi.saveBands(
        data.id,
        rows.map(({ key, ...b }) => {
          void key;
          return b;
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instrument', data.id] });
      setEditing(false);
      setError(null);
      setSaved(true);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Gagal menyimpan kategori.'),
  });

  const update = (key: string, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () =>
    setRows((rs) => [
      ...rs,
      { key: `new-${Date.now()}`, gt: null, name: '', nameIndividual: null, characteristic: null },
    ]);

  const removeRow = (key: string) => setRows((rs) => rs.filter((r) => r.key !== key));

  // Urutan tampilan selalu dari ambang tertinggi — sama seperti urutan
  // pembacaan mesin skoring, supaya yang dilihat admin adalah yang dieksekusi.
  const ordered = [...rows].sort((a, b) => (b.gt ?? -Infinity) - (a.gt ?? -Infinity));
  const catchAllCount = rows.filter((r) => r.gt === null).length;
  const problem =
    catchAllCount !== 1
      ? 'Harus ada tepat satu kategori terbawah yang kolom "lebih dari"-nya kosong.'
      : rows.some((r) => !r.name.trim())
        ? 'Masih ada kategori tanpa nama.'
        : null;

  return (
    <div className="flex flex-col gap-4">
      {saved && !editing && <Banner tone="success">Kategori tersimpan.</Banner>}
      {error && <Banner tone="danger">{error}</Banner>}

      <Card padded={false} className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h2 className="text-[17px] font-semibold text-ink">Ambang kategori indeks</h2>
            <p className="mt-0.5 text-[13px] text-ink-secondary">
              Rentang tampilan dihitung otomatis dari kolom "lebih dari".
            </p>
          </div>

          {canEdit &&
            (editing ? (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setEditing(false);
                    setError(null);
                    setRows(
                      data.version.bands.map((b, i) => ({
                        key: `b${i}`,
                        gt: b.gt,
                        name: b.name,
                        nameIndividual: b.nameIndividual,
                        characteristic: b.characteristic,
                      })),
                    );
                  }}
                  disabled={save.isPending}
                >
                  Batal
                </Button>
                <Button
                  size="sm"
                  onClick={() => save.mutate()}
                  loading={save.isPending}
                  disabled={problem !== null}
                >
                  Simpan
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setEditing(true);
                  setSaved(false);
                }}
              >
                Ubah Kategori
              </Button>
            ))}
        </div>

        {editing && problem && (
          <div className="px-5 pt-4">
            <Banner tone="danger">{problem}</Banner>
          </div>
        )}

        <div className="overflow-x-auto">
          <table
            className={cn('w-full border-collapse text-left', editing ? 'min-w-[900px]' : 'min-w-[520px]')}
          >
            <thead>
              <tr className="border-b border-border">
                {editing && <Th className="w-32">Lebih dari (&gt;)</Th>}
                <Th className="w-32">Rentang</Th>
                <Th>Kategori{hasIndividualNames && ' (TBM)'}</Th>
                {hasIndividualNames && <Th>Kategori (Individu)</Th>}
                <Th>Definisi</Th>
                {editing && <Th className="w-20">Aksi</Th>}
              </tr>
            </thead>
            <tbody>
              {ordered.map((row, i) => {
                const upper = i === 0 ? 100 : (ordered[i - 1]!.gt ?? 100);
                const display = row.gt === null ? `<${upper + 1}` : `${row.gt + 1} - ${upper}`;

                return (
                  <tr key={row.key} className="border-b border-border align-top last:border-0">
                    {editing && (
                      <Td>
                        <Input
                          label="Lebih dari"
                          hideLabel
                          type="number"
                          inputMode="numeric"
                          value={row.gt === null ? '' : String(row.gt)}
                          placeholder="kosong = terbawah"
                          onChange={(e) =>
                            update(row.key, {
                              gt: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                        />
                      </Td>
                    )}
                    <Td>
                      <span className="tabular font-medium text-ink">{display}</span>
                    </Td>
                    <Td>
                      {editing ? (
                        <Input
                          label="Kategori"
                          hideLabel
                          value={row.name}
                          onChange={(e) => update(row.key, { name: e.target.value })}
                        />
                      ) : (
                        <span className="font-medium text-ink">{row.name}</span>
                      )}
                    </Td>
                    {hasIndividualNames && (
                      <Td>
                        {editing ? (
                          <Input
                            label="Kategori individu"
                            hideLabel
                            value={row.nameIndividual ?? ''}
                            onChange={(e) =>
                              update(row.key, { nameIndividual: e.target.value || null })
                            }
                          />
                        ) : (
                          (row.nameIndividual ?? '—')
                        )}
                      </Td>
                    )}
                    <Td>
                      {editing ? (
                        <Textarea
                          label="Definisi"
                          hideLabel
                          className="min-h-16"
                          placeholder="Deskripsi definisi kategori capaian..."
                          value={row.characteristic ?? ''}
                          onChange={(e) =>
                            update(row.key, { characteristic: e.target.value || null })
                          }
                        />
                      ) : (
                        <span className="text-[14px] text-ink-secondary">
                          {row.characteristic ?? '—'}
                        </span>
                      )}
                    </Td>
                    {editing && (
                      <Td>
                        <Button size="sm" variant="ghost" onClick={() => removeRow(row.key)}>
                          Hapus
                        </Button>
                      </Td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {editing && (
          <div className="border-t border-border px-5 py-3">
            <Button size="sm" variant="secondary" onClick={addRow}>
              + Kategori
            </Button>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="mb-2 text-[17px] font-semibold">Kenapa kolomnya "lebih dari", bukan rentang</h2>
        <p className="text-[15px] text-ink-secondary">
          Kategori ditentukan dari nilai <strong>desimal</strong>, bukan hasil pembulatan. Indeks
          70,4 masuk <em>Mandiri</em> karena 70,4 &gt; 70 — kalau ditulis sebagai rentang "71–85"
          lalu dibulatkan lebih dulu jadi 70, kategorinya turun ke <em>Berkembang</em>. Angka
          "lebih dari" itulah yang benar-benar dipakai mesin; rentang di sebelahnya hanya tampilan
          yang diturunkan darinya, jadi keduanya tidak bisa berselisih.
        </p>
      </Card>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-3 text-[13px] font-semibold text-ink-secondary', className)}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3.5 align-top">{children}</td>;
}
